import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import { Box, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import type { FC, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useIsHalloweenSeason } from "@/hooks/useIsHalloweenSeason";
import { ASSISTANT_AVATAR, FONT_SIZE, ICON_TILE, MOTION } from "@/theme/tokens";
import { AssistantAvatar } from "@/ui/AssistantAvatar/AssistantAvatar";
import { type AssistantAvatarConfig, AssistantAvatarMood } from "@/ui/AssistantAvatar/assistantAvatarConfig";
import { OneTimeNoticeContent } from "@/ui/molecules/OneTimeNotice";
import SystemButton from "@/ui/SystemButton/SystemButton";
import { defaultCharacterAvatar } from "./AnnieStudio/annieAvatarPresets";
import type { AnnieStudioNudge } from "./AnnieStudio/useAnnieStudioNudge";

type Props = {
	disabled: boolean;
	onPromptSelect: (prompt: string) => void;
	/** The user's Annie, greeting in place of the icon tile; null keeps the standard tile. */
	avatarConfig: AssistantAvatarConfig | null;
	studioNudge: AnnieStudioNudge;
	onCustomizeFromNudge: () => void;
};

type Capability = {
	descriptionKey: string;
	icon: ReactNode;
	labelKey: string;
	promptKey: string;
};

const capabilities: Capability[] = [
	{
		labelKey: "annie.opening.capabilities.understand.title",
		descriptionKey: "annie.opening.capabilities.understand.description",
		promptKey: "annie.opening.capabilities.understand.prompt",
		icon: <InsightsOutlinedIcon />,
	},
	{
		labelKey: "annie.opening.capabilities.plan.title",
		descriptionKey: "annie.opening.capabilities.plan.description",
		promptKey: "annie.opening.capabilities.plan.prompt",
		icon: <PeopleAltOutlinedIcon />,
	},
	{
		labelKey: "annie.opening.capabilities.change.title",
		descriptionKey: "annie.opening.capabilities.change.description",
		promptKey: "annie.opening.capabilities.change.prompt",
		icon: <SwapHorizIcon />,
	},
];

const NUDGE_AVATAR_SIZE = ASSISTANT_AVATAR.SIZE.NUDGE;

const GREETING_AVATAR_SIZE = ASSISTANT_AVATAR.SIZE.GREETING;

/** An outcome-based capability menu for schedulers learning what Annie can do. */
export const AnnieEmptyStateCapabilities: FC<Props> = ({ disabled, onPromptSelect, avatarConfig, studioNudge, onCustomizeFromNudge }) => {
	const theme = useTheme();
	const { t } = useTranslation();
	const nudgeAvatar = defaultCharacterAvatar(useIsHalloweenSeason());

	return (
		<Box data-testid="annie-empty-state-capabilities" sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2.5, pt: 1 }}>
			<Box sx={{ textAlign: "center", maxWidth: 440 }}>
				{avatarConfig === null ? (
					<Box
						sx={{
							width: ICON_TILE.MD.size,
							height: ICON_TILE.MD.size,
							mx: "auto",
							mb: 1.5,
							display: "grid",
							placeItems: "center",
							borderRadius: 2,
							bgcolor: alpha(theme.palette.primary.main, 0.1),
							color: "primary.main",
							"& > svg": { fontSize: ICON_TILE.MD.glyph },
						}}
					>
						<InsightsOutlinedIcon />
					</Box>
				) : (
					<Box sx={{ width: GREETING_AVATAR_SIZE, mx: "auto", mb: 1.5 }}>
						<AssistantAvatar config={avatarConfig} mood={AssistantAvatarMood.Idle} size={GREETING_AVATAR_SIZE} animated dataTestId="annie-greeting-avatar" />
					</Box>
				)}
				<Typography component="h2" variant="h5" sx={{ fontWeight: 750, letterSpacing: "-0.03em" }}>
					{t("annie.opening.capabilities.title")}
				</Typography>
				<Typography sx={{ mt: 0.75, color: "text.secondary", fontSize: FONT_SIZE.EMPHASIS }}>{t("annie.opening.capabilities.hint")}</Typography>
			</Box>

			<OneTimeNoticeContent
				isVisible={studioNudge.isVisible}
				onAcknowledge={studioNudge.dismiss}
				dismissLabel={t("annie.studio.nudge.dismiss")}
				tone="neutral"
				icon={
					<AssistantAvatar
						config={nudgeAvatar}
						mood={AssistantAvatarMood.Idle}
						size={NUDGE_AVATAR_SIZE}
						animated={false}
						dataTestId="annie-studio-nudge-avatar"
					/>
				}
				leadingAction={
					<SystemButton
						buttonVariant="borderless"
						size="small"
						noFullWidthMobile
						onClick={onCustomizeFromNudge}
						sx={{ "&&&": { color: "primary.main", fontWeight: 700 } }}
						data-testid="annie-studio-nudge-customize"
					>
						{t("annie.studio.nudge.action")}
					</SystemButton>
				}
				dataTestId="annie-studio-nudge"
				sx={{ alignItems: "center" }}
			>
				{t("annie.studio.nudge.text")}
			</OneTimeNoticeContent>

			<Box sx={{ width: "100%", display: "grid", gap: 1 }}>
				{capabilities.map((capability, index) => (
					<Box key={capability.labelKey} sx={{ "& > button": { width: "100%" } }}>
						<SystemButton
							buttonVariant="secondary"
							disabled={disabled}
							onClick={() => onPromptSelect(t(capability.promptKey))}
							data-testid={`annie-capability-prompt-${index + 1}`}
							sx={{
								width: "100%",
								height: "auto",
								minHeight: 68,
								px: 1.5,
								py: 1.25,
								gap: 1.25,
								justifyContent: "flex-start",
								borderColor: alpha(theme.palette.primary.main, 0.2),
								transition: `transform ${MOTION.DURATION.CONTROL}ms ${MOTION.EASE_OUT}, border-color ${MOTION.DURATION.HOVER}ms ease`,
								"&:active": { transform: "scale(0.98)" },
							}}
						>
							<Box
								sx={{
									width: ICON_TILE.SM.size,
									height: ICON_TILE.SM.size,
									flex: "0 0 auto",
									display: "grid",
									placeItems: "center",
									borderRadius: 1.5,
									bgcolor: alpha(theme.palette.primary.main, 0.09),
									color: "primary.main",
									"& > svg": { fontSize: ICON_TILE.SM.glyph },
								}}
							>
								{capability.icon}
							</Box>
							<Box sx={{ minWidth: 0, flex: 1, textAlign: "start" }}>
								<Typography sx={{ fontWeight: 750, fontSize: FONT_SIZE.TITLE_SM }}>{t(capability.labelKey)}</Typography>
								<Typography sx={{ color: "text.secondary", fontSize: FONT_SIZE.SECONDARY }}>{t(capability.descriptionKey)}</Typography>
							</Box>
						</SystemButton>
					</Box>
				))}
			</Box>
		</Box>
	);
};
